import { useSession } from "@/components/providers/session";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { recordConversion, Conversion } from "@/utils/conversions";

export default function Contact({ franchise = false }) {
  const [state, setState] = useState({
    complete: null,
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const session = useSession();

  const courses =
    session && session.selectedCourses && session.selectedCourses.length > 0
      ? session.selectedCourses.map((course) => {
          return { id: course.event.id, title: course.fields.title };
        })
      : [];

  const onSubmit = async (data) => {
    try {
      const req = franchise
        ? await fetch("/api/franchise", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...data, courses }),
          })
        : await fetch("/api/contact", {
            method: "post",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...data, courses }),
          });
      const res = await req.json();
      if (res && res.success) {
        recordConversion({
          send_to: Conversion.contact,
        });
        setState({
          complete: true,
        });
        (window as any).fbq && (window as any).fbq("track", "Contact");
        (window as any).heap &&
          (window as any).heap.addUserProperties({
            ...data,
          });
        (window as any).heap &&
          (window as any).heap.track("Contact", {
            ...data,
          });
        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "identify",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $organization: data.company,
              $phone_number: data.phone_number,
            },
          ]);
        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "track",
            "Contact",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone_number,
              $message: data.message,
            },
          ]);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const watchOptin = watch("optIn", false);

  return (
    <div className="bg-white px-4 overflow-hidden sm:px-6 lg:px-8 lg:pb-24">
      <div className="relative max-w-xl mx-auto">
        <svg
          className="absolute left-full transform translate-x-1/2"
          width="404"
          height="404"
          fill="none"
          viewBox="0 0 404 404"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="85737c0e-0916-41d7-917f-596dc7edfa27"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            width="404"
            height="404"
            fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
          />
        </svg>
        <svg
          className="absolute right-full bottom-0 transform -translate-x-1/2"
          width="404"
          height="404"
          fill="none"
          viewBox="0 0 404 404"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="85737c0e-0916-41d7-917f-596dc7edfa27"
              x="0"
              y="0"
              width="20"
              height="20"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0"
                y="0"
                width="4"
                height="4"
                className="text-gray-200"
                fill="currentColor"
              />
            </pattern>
          </defs>
          <rect
            width="404"
            height="404"
            fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
          />
        </svg>

        {state.complete && (
          <div className="mb-24">
            <div className="bg-white">
              <div className="max-w-7xl mx-auto pt-24 pb-24 px-4 sm:px-6 md:py-16 lg:py-24 lg:px-8">
                <div className="text-center">
                  <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
                    Your contact submission has been received and our team will
                    be back in touch as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {!state.complete && (
          <div className="mb-24">
            <form
              onSubmit={handleSubmit(onSubmit)}
              action="#"
              method="POST"
              className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
            >
              <div>
                <label htmlFor="first_name" className="sr-only">
                  First name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="first_name"
                    id="first_name"
                    autoComplete="given-name"
                    className={`py-3 px-4 block w-full shadow focus:ring-brand-500 focus:border-brand-500 ${
                      errors.first_name ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    {...register("first_name", { required: true })}
                    placeholder="First name*"
                  />
                  {errors.first_name && (
                    <span className="text-red-500 pt-2 text-sm block">
                      First name is required
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="last_name" className="sr-only">
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="last_name"
                    id="last_name"
                    autoComplete="family-name"
                    className={`py-3 px-4 block w-full shadow focus:ring-brand-500 focus:border-brand-500 ${
                      errors.last_name ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    {...register("last_name", { required: true })}
                    placeholder="Last name*"
                  />
                  {errors.last_name && (
                    <span className="text-red-500 pt-2 text-sm block">
                      Last name is required
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="company" className="sr-only">
                  Company
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    autoComplete="organization"
                    className="py-3 px-4 block w-full shadow focus:ring-brand-500 focus:border-brand-500 border-gray-300 rounded-md"
                    ref={register}
                    {...register("company")}
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`py-3 px-4 block w-full shadow focus:ring-brand-500 focus:border-brand-500 ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    {...register("email", { required: true })}
                    placeholder="Email address*"
                  />
                  {errors.email && (
                    <span className="text-red-500 pt-2 text-sm block">
                      Email is required
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="phone_number" className="sr-only">
                  Phone Number
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type="text"
                    name="phone_number"
                    id="phone_number"
                    autoComplete="tel"
                    className={`py-3 px-4 block w-full focus:ring-brand-500 focus:border-brand-500 ${
                      errors.phone_number ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    placeholder="Phone number*"
                    {...register("phone_number", { required: true })}
                  />
                  {errors.phone_number && (
                    <span className="text-red-500 pt-2 text-sm block">
                      Phone number is required
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="message" className="sr-only">
                  Message
                </label>
                <div className="mt-1">
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className={`sm:py-3 px-4 block w-full shadow focus:ring-brand-500 focus:border-brand-500 ${
                      errors.message ? "border-red-500" : "border-gray-300"
                    } rounded-md`}
                    {...register("message", { required: true })}
                    placeholder="Your enquiry*"
                  ></textarea>
                  {errors.message && (
                    <span className="text-red-500 pt-2 text-sm block">
                      Message is required
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-start">
                  <div className="shrink-0 relative">
                    <input
                      id="optin"
                      type="checkbox"
                      name="optIn"
                      {...register("optIn", { required: true })}
                      className="absolute inset-0 z-10 w-full h-full opacity-0"
                    />
                    <button
                      type="button"
                      aria-pressed="false"
                      className={`${
                        watchOptin ? "bg-brand-600" : "bg-gray-200"
                      } relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500`}
                    >
                      <span className="sr-only">Agree to policies</span>
                      <span
                        aria-hidden="true"
                        className={`${
                          watchOptin ? "translate-x-5" : "translate-x-0"
                        } inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                      ></span>
                    </button>
                  </div>
                  <div className="ml-3">
                    <label htmlFor="optin" className="text-base text-gray-500">
                      By selecting this, you agree to the{" "}
                      <Link
                        href="/privacy-policy/"
                        className="font-medium text-gray-700 underline"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </label>
                  </div>
                </div>
                {errors.optIn && (
                  <span className="text-red-500 pt-2 text-sm block">
                    You must agree to our privacy policy
                  </span>
                )}
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow text-base font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
                >
                  Let&#39;s talk
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
